// XSL stylesheet for rendering a laboratory order as the printable Lab Order Request.
//
// Healthlink has its own stylesheet for this message family — a retrieved order names it
// (`LabOrderPrint_GP_2.20.xsl`) — and the backend attaches it as `xslContent` when it can
// be fetched (KIN-1535). This is the fallback for when it cannot, so it deliberately
// mirrors that print rather than inventing a second look: two-column header, the barcode,
// the order number, the specimen and test block.
//
// The barcode is not drawn here. It travels inside the message as a base64 GIF, in the
// OBX whose OBX.3 names it BARCODE — the same image Healthlink's own print uses.
//
// Fed through HealthlinkXsltFrame (XSLTProcessor + sandboxed iframe), so the styling
// lives inline in the transform output.
export const LAB_ORDER_MESSAGE_XSL = `<?xml version="1.0" encoding="UTF-8"?>
<xsl:stylesheet version="1.0"
  xmlns:xsl="http://www.w3.org/1999/XSL/Transform"
  xmlns:hl7="urn:hl7-org:v2xml">
  <xsl:output method="html" omit-xml-declaration="yes" indent="yes"/>

  <xsl:template name="formatDate">
    <xsl:param name="value"/>
    <xsl:if test="string-length($value) &gt;= 8">
      <xsl:value-of select="substring($value, 7, 2)"/>/<xsl:value-of select="substring($value, 5, 2)"/>/<xsl:value-of select="substring($value, 1, 4)"/>
    </xsl:if>
  </xsl:template>

  <xsl:template name="formatDateTime">
    <xsl:param name="value"/>
    <xsl:call-template name="formatDate">
      <xsl:with-param name="value" select="$value"/>
    </xsl:call-template>
    <xsl:if test="string-length($value) &gt;= 12">
      <xsl:text> </xsl:text>
      <xsl:value-of select="substring($value, 9, 2)"/>:<xsl:value-of select="substring($value, 11, 2)"/>
    </xsl:if>
  </xsl:template>

  <!-- HL7 table 0001 for PID.8, spelt out the way the print does. -->
  <xsl:template name="formatGender">
    <xsl:param name="value"/>
    <xsl:choose>
      <xsl:when test="$value = 'F'">Female</xsl:when>
      <xsl:when test="$value = 'M'">Male</xsl:when>
      <xsl:otherwise><xsl:value-of select="$value"/></xsl:otherwise>
    </xsl:choose>
  </xsl:template>

  <!-- OBR.27 TQ.6, HL7 table 0485. R is by far the common case. -->
  <xsl:template name="formatPriority">
    <xsl:param name="value"/>
    <xsl:choose>
      <xsl:when test="$value = 'R'">Routine</xsl:when>
      <xsl:when test="$value = 'S'">Stat</xsl:when>
      <xsl:when test="$value = 'A'">ASAP</xsl:when>
      <xsl:when test="$value = 'P'">Preoperative</xsl:when>
      <xsl:otherwise><xsl:value-of select="$value"/></xsl:otherwise>
    </xsl:choose>
  </xsl:template>

  <xsl:template name="row">
    <xsl:param name="label"/>
    <xsl:param name="value"/>
    <xsl:if test="string-length($value) &gt; 0">
      <tr><td class="k"><xsl:value-of select="$label"/></td><td class="v"><xsl:value-of select="$value"/></td></tr>
    </xsl:if>
  </xsl:template>

  <xsl:template match="/">
    <div class="lab-print">
      <style>
        .lab-print { color: #111827; font-size: 12px; line-height: 1.45; }
        .lab-print h1 { font-size: 20px; font-weight: 700; margin: 0 0 2px; text-align: center; }
        .lab-print .ref { color: #374151; font-size: 11px; margin: 0 0 10px; text-align: center; }
        .lab-print hr { border: none; border-top: 1px solid #9ca3af; margin: 8px 0; }
        /* Left, not right: the viewer overlays its own Print button in the top-right
           corner, and a right-aligned barcode collides with it. */
        .lab-print .top { display: flex; justify-content: flex-start; margin-bottom: 4px; }
        .lab-print .top img { height: 44px; }
        .lab-print .cols { display: flex; gap: 32px; }
        .lab-print .col { flex: 1 1 0; min-width: 0; }
        .lab-print .col h2 { font-size: 12px; font-weight: 700; margin: 0 0 4px; text-align: center; }
        .lab-print table { border-collapse: collapse; width: 100%; }
        .lab-print td { padding: 2px 6px 2px 0; vertical-align: top; }
        .lab-print td.k { color: #111827; font-weight: 700; white-space: nowrap; width: 42%; }
        .lab-print td.v { color: #1f2937; word-break: break-word; }
        .lab-print .band { display: flex; gap: 32px; }
        .lab-print .order-no { font-weight: 700; white-space: nowrap; }
        .lab-print .tests { margin: 0; padding-left: 18px; }
        .lab-print .tests li { font-weight: 700; }
        .lab-print .summary { color: #1f2937; }
      </style>
      <xsl:apply-templates select="hl7:OML_O21 | hl7:ORL_O22"/>
    </div>
  </xsl:template>

  <xsl:template match="hl7:OML_O21">
    <!-- The barcode Healthlink embedded in the message, when it is there. -->
    <xsl:variable name="barcode"
      select="(.//hl7:OBX[hl7:OBX.3/hl7:CE.2 = 'BARCODE']/hl7:OBX.5/hl7:CE.2)[1]"/>
    <xsl:if test="string-length($barcode) &gt; 0">
      <div class="top">
        <img alt="Order barcode">
          <xsl:attribute name="src">data:image/gif;base64,<xsl:value-of select="$barcode"/></xsl:attribute>
        </img>
      </div>
    </xsl:if>

    <h1>Lab Order Request</h1>
    <p class="ref">
      <xsl:text>Order Number: </xsl:text>
      <span class="order-no"><xsl:value-of select="hl7:MSH/hl7:MSH.10"/></span>
    </p>
    <hr/>

    <div class="cols">
      <div class="col">
        <h2>Patient Demographics</h2>
        <table>
          <tr>
            <td class="k">Patient Name:</td>
            <td class="v">
              <xsl:value-of select=".//hl7:PID/hl7:PID.5/hl7:XPN.2"/>
              <xsl:text> </xsl:text>
              <xsl:value-of select=".//hl7:PID/hl7:PID.5/hl7:XPN.1/hl7:FN.1"/>
            </td>
          </tr>
          <tr>
            <td class="k">Date of Birth:</td>
            <td class="v">
              <xsl:call-template name="formatDate">
                <xsl:with-param name="value" select=".//hl7:PID/hl7:PID.7/hl7:TS.1"/>
              </xsl:call-template>
            </td>
          </tr>
          <tr>
            <td class="k">Gender:</td>
            <td class="v">
              <xsl:call-template name="formatGender">
                <xsl:with-param name="value" select=".//hl7:PID/hl7:PID.8"/>
              </xsl:call-template>
            </td>
          </tr>
          <tr>
            <td class="k">Address:</td>
            <td class="v">
              <xsl:for-each select=".//hl7:PID/hl7:PID.11/hl7:XAD.1/hl7:SAD.1 | .//hl7:PID/hl7:PID.11/hl7:XAD.2 | .//hl7:PID/hl7:PID.11/hl7:XAD.3 | .//hl7:PID/hl7:PID.11/hl7:XAD.4 | .//hl7:PID/hl7:PID.11/hl7:XAD.5">
                <xsl:if test="string-length(.) &gt; 0">
                  <xsl:value-of select="."/><br/>
                </xsl:if>
              </xsl:for-each>
            </td>
          </tr>
          <xsl:call-template name="row">
            <xsl:with-param name="label" select="'PMS:'"/>
            <xsl:with-param name="value" select=".//hl7:PID/hl7:PID.3/hl7:CX.1"/>
          </xsl:call-template>
        </table>
      </div>

      <div class="col">
        <h2>General Practitioner Details</h2>
        <table>
          <xsl:call-template name="row">
            <xsl:with-param name="label" select="'GP:'"/>
            <xsl:with-param name="value" select="hl7:MSH/hl7:MSH.4/hl7:HD.1"/>
          </xsl:call-template>
          <xsl:call-template name="row">
            <xsl:with-param name="label" select="'Medical Council Number:'"/>
            <xsl:with-param name="value" select="hl7:MSH/hl7:MSH.4/hl7:HD.2"/>
          </xsl:call-template>
          <xsl:call-template name="row">
            <xsl:with-param name="label" select="'Practice Details:'"/>
            <xsl:with-param name="value" select=".//hl7:PV1/hl7:PV1.3/hl7:PL.4/hl7:HD.1"/>
          </xsl:call-template>
          <tr>
            <td class="k">Address:</td>
            <td class="v">
              <xsl:for-each select=".//hl7:ORC/hl7:ORC.22/hl7:XAD.1/hl7:SAD.1 | .//hl7:ORC/hl7:ORC.22/hl7:XAD.2 | .//hl7:ORC/hl7:ORC.22/hl7:XAD.3 | .//hl7:ORC/hl7:ORC.22/hl7:XAD.4">
                <xsl:if test="string-length(.) &gt; 0">
                  <xsl:value-of select="."/><br/>
                </xsl:if>
              </xsl:for-each>
            </td>
          </tr>
          <xsl:call-template name="row">
            <xsl:with-param name="label" select="'Hospital:'"/>
            <xsl:with-param name="value" select="hl7:MSH/hl7:MSH.6/hl7:HD.1"/>
          </xsl:call-template>
          <xsl:call-template name="row">
            <xsl:with-param name="label" select="'Healthlink Message ID:'"/>
            <xsl:with-param name="value" select="hl7:MSH/hl7:MSH.10"/>
          </xsl:call-template>
          <tr>
            <td class="k">Received:</td>
            <td class="v">
              <xsl:call-template name="formatDateTime">
                <xsl:with-param name="value" select="hl7:MSH/hl7:MSH.7/hl7:TS.1"/>
              </xsl:call-template>
            </td>
          </tr>
        </table>
      </div>
    </div>

    <hr/>

    <table>
      <xsl:if test="string-length(.//hl7:ORC/hl7:ORC.14/hl7:XTN.1) &gt; 0">
        <tr>
          <td class="k">GP Out Of Hours Number:</td>
          <td class="v"><xsl:value-of select=".//hl7:ORC/hl7:ORC.14/hl7:XTN.1"/></td>
        </tr>
      </xsl:if>
      <tr>
        <td class="k">Priority:</td>
        <td class="v">
          <xsl:call-template name="formatPriority">
            <xsl:with-param name="value" select="(.//hl7:OBR/hl7:OBR.27/hl7:TQ.6)[1]"/>
          </xsl:call-template>
        </td>
      </tr>
      <xsl:call-template name="row">
        <xsl:with-param name="label" select="'Clinical Details:'"/>
        <xsl:with-param name="value" select="(.//hl7:OBR/hl7:OBR.13)[1]"/>
      </xsl:call-template>
    </table>

    <hr/>

    <div class="band">
      <table>
        <xsl:call-template name="row">
          <xsl:with-param name="label" select="'Specimen:'"/>
          <xsl:with-param name="value" select="(.//hl7:OBR/hl7:OBR.15/hl7:SPS.1/hl7:CE.2)[1]"/>
        </xsl:call-template>
        <tr>
          <td class="k">Procedure and Description:</td>
          <td class="v">
            <xsl:choose>
              <xsl:when test=".//hl7:OBR/hl7:OBR.4/hl7:CE.2">
                <ol class="tests">
                  <xsl:for-each select=".//hl7:OBR">
                    <li>
                      <xsl:value-of select="hl7:OBR.4/hl7:CE.2"/>
                      <xsl:if test="string-length(hl7:OBR.4/hl7:CE.1) &gt; 0">
                        <xsl:text> (</xsl:text><xsl:value-of select="hl7:OBR.4/hl7:CE.1"/><xsl:text>)</xsl:text>
                      </xsl:if>
                    </li>
                  </xsl:for-each>
                </ol>
              </xsl:when>
              <xsl:otherwise>
                <span>Not yet chosen — the tests are selected in Healthlink.</span>
              </xsl:otherwise>
            </xsl:choose>
          </td>
        </tr>
        <tr>
          <td class="k">Date/Time Collected:</td>
          <td class="v">
            <xsl:call-template name="formatDateTime">
              <xsl:with-param name="value" select="(.//hl7:OBR/hl7:OBR.7/hl7:TS.1)[1]"/>
            </xsl:call-template>
          </td>
        </tr>
      </table>
    </div>

    <hr/>

    <table>
      <tr>
        <td class="k">Patient Summary:</td>
        <td class="v summary">
          <xsl:value-of select=".//hl7:PID/hl7:PID.5/hl7:XPN.2"/>
          <xsl:text> </xsl:text>
          <xsl:value-of select=".//hl7:PID/hl7:PID.5/hl7:XPN.1/hl7:FN.1"/>
          <xsl:text> (</xsl:text>
          <xsl:call-template name="formatDate">
            <xsl:with-param name="value" select=".//hl7:PID/hl7:PID.7/hl7:TS.1"/>
          </xsl:call-template>
          <xsl:text> - </xsl:text>
          <xsl:value-of select=".//hl7:PID/hl7:PID.8"/>
          <xsl:text>), </xsl:text>
          <xsl:value-of select=".//hl7:PID/hl7:PID.11/hl7:XAD.1/hl7:SAD.1"/>
          <xsl:if test="string-length(.//hl7:PID/hl7:PID.11/hl7:XAD.2) &gt; 0">
            <xsl:text>, </xsl:text><xsl:value-of select=".//hl7:PID/hl7:PID.11/hl7:XAD.2"/>
          </xsl:if>
        </td>
      </tr>
    </table>
  </xsl:template>

  <xsl:template match="hl7:ORL_O22">
    <h1>Laboratory Acknowledgement</h1>
    <p class="ref">
      <xsl:text>For order </xsl:text>
      <span class="order-no"><xsl:value-of select=".//hl7:MSA/hl7:MSA.2"/></span>
    </p>
    <hr/>
    <table>
      <xsl:call-template name="row">
        <xsl:with-param name="label" select="'Acknowledgement code:'"/>
        <xsl:with-param name="value" select=".//hl7:MSA/hl7:MSA.1"/>
      </xsl:call-template>
      <xsl:call-template name="row">
        <xsl:with-param name="label" select="'Reason:'"/>
        <xsl:with-param name="value" select=".//hl7:MSA/hl7:MSA.3"/>
      </xsl:call-template>
      <xsl:call-template name="row">
        <xsl:with-param name="label" select="'Order control:'"/>
        <xsl:with-param name="value" select=".//hl7:ORC/hl7:ORC.1"/>
      </xsl:call-template>
      <xsl:call-template name="row">
        <xsl:with-param name="label" select="'Error code:'"/>
        <xsl:with-param name="value" select=".//hl7:ERR/hl7:ERR.1/hl7:ELD.4/hl7:CE.1"/>
      </xsl:call-template>
      <xsl:call-template name="row">
        <xsl:with-param name="label" select="'Error description:'"/>
        <xsl:with-param name="value" select=".//hl7:ERR/hl7:ERR.1/hl7:ELD.4/hl7:CE.2"/>
      </xsl:call-template>
    </table>
  </xsl:template>
</xsl:stylesheet>`;
